import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "p-2"
}
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = {
  key: 1,
  class: "onboard-course"
}
const _hoisted_6 = { class: "onboard-course__grid" }
const _hoisted_7 = ["for"]
const _hoisted_8 = ["onUpdate:modelValue", "id", "value"]
const _hoisted_9 = { class: "onboard-course__thumb" }
const _hoisted_10 = ["src"]
const _hoisted_11 = {
  class: "onboard-course__check",
  "aria-hidden": "true"
}
const _hoisted_12 = { class: "onboard-course__name" }
const _hoisted_13 = { class: "onboard-course__footer" }
const _hoisted_14 = { class: "onboard-course__count" }
const _hoisted_15 = { key: 2 }
const _hoisted_16 = ["innerHTML"]

import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { headerState } from '../../stores/header'
import { userState } from '../../stores/user'
import { ROUTER_NAME } from '../../router'
import api from '../../../api/v2/api'
import { snackBarState } from '../../stores/snackbar'
import { LETTER_FORMAT } from '../../utils/constants'
import { checkMobile } from '../../utils/utils'
import { UserOnboardType } from '../../../api/v2/models/user'
import { getQueryString } from '../../../../helpers/route-helper'

type StepState = typeof STEP_FLOW[number]

export default /*@__PURE__*/_defineComponent({
  __name: 'OnBoarding',
  setup(__props) {

const router = useRouter()
const route = useRoute()

const mobile = ref(checkMobile())
const state = ref<'welcome' | 'course' | 'contact_form'>('welcome')
const previewUserOnboard = ref<UserOnboardType | null>(null)

const schoolSetting = computed(() => headerState.schoolSetting)
const user = computed(() => userState.user)
const userOnboard = computed(() => previewUserOnboard.value ?? user.value?.userOnboard)

const previewCode = computed(() => {
  return getQueryString(route.query, 'preview_code') ?? ''
})

// Step flow in display order. `enabledSteps` from the API uses 'questionnaire',
// which maps to the 'contact_form' state below.
const STEP_FLOW = ['welcome', 'course', 'contact_form'] as const
const ENABLED_TO_STATE: Record<string, StepState> = {
  welcome: 'welcome',
  course: 'course',
  questionnaire: 'contact_form'
}

// Only the steps the admin enabled, kept in display order.
// Fall back to all steps when nothing is configured.
const enabledStates = computed<StepState[]>(() => {
  const enabled = userOnboard.value?.enabledSteps
  if (!enabled || enabled.length === 0) return [...STEP_FLOW]
  return STEP_FLOW.filter((s) => enabled.some((e) => ENABLED_TO_STATE[e] === s))
})

const currentIndex = computed(() => enabledStates.value.indexOf(state.value))
const isFirstStep = computed(() => currentIndex.value <= 0)
const selectedCount = computed(() => userOnboard.value?.courses?.filter((item) => item.selected).length ?? 0)

const enrollSelectedCourses = () => {
  userOnboard.value?.courses?.forEach((item: any) => {
    if (item.selected) {
      api.enrollment.create({
        user_id: user.value?.id,
        course_id: item.id
      })
    }
  })
}

// Must run whenever the questionnaire step is shown, including when it is the
// first enabled step (previously this was only wired up when leaving 'course').
const setupContactFormCallback = () => {
  ;(window as any).onContactFormCheckInputSuccess = () => {
    // Wait submit complete
    userState.isLoading = true
    setTimeout(async () => {
      if (previewUserOnboard.value) {
        router.push({ name: ROUTER_NAME.HOME })
        snackBarState.setPushSnackbar('オンボーディングプレビューが完了しました。')
      } else {
        await userState.getUser()
        previewUserOnboard.value = null
        if (!userOnboard.value) {
          snackBarState.setPushSnackbar('ありがとございました')
        }
        router.push({ name: ROUTER_NAME.HOME })
      }
    }, 2000)
  }
}

const goToState = () => {
  router.push({
    hash: state.value,
    query: route.query
  })
}

const enterState = (next: StepState) => {
  state.value = next
  goToState()
  if (next === 'contact_form') setupContactFormCallback()
}

const goNext = () => {
  if (state.value === 'course') enrollSelectedCourses()
  const next = enabledStates.value[currentIndex.value + 1]
  if (!next) return
  enterState(next)
}

const goBack = () => {
  const prev = enabledStates.value[currentIndex.value - 1]
  if (!prev) return
  enterState(prev)
}

// Pick the starting step from the URL hash when valid, otherwise the first
// enabled step. Runs once, as soon as the onboarding data is available.
let stateInitialized = false
const initState = () => {
  if (stateInitialized || !userOnboard.value) return
  stateInitialized = true
  const hash = route.hash ? (route.hash.replace('#', '') as StepState) : null
  const start = hash && enabledStates.value.includes(hash) ? hash : (enabledStates.value[0] ?? 'welcome')
  state.value = start
  if (start === 'contact_form') setupContactFormCallback()
}

watch(user, () => {
  if (!userOnboard.value && !previewCode.value) {
    router.push({ name: ROUTER_NAME.HOME })
  }
})

watch(userOnboard, initState, { immediate: true })

onMounted(async () => {
  headerState.setTitle('マイプロファイル')
  headerState.getSchoolSetting()

  if (previewCode.value) {
    previewUserOnboard.value = await api.userOnboard.index({ previewCode: previewCode.value })
    initState()
  }
})

const goToHome = () => {
  router.push({ name: ROUTER_NAME.HOME })
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    (user.value && userOnboard.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", {
              class: _normalizeClass(["white-back", state.value === 'course' ? 'col-12' : 'col-md-8'])
            }, [
              (state.value === 'welcome')
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createElementVNode("div", {
                      innerHTML: userOnboard.value.welcomeBody
                    }, null, 8 /* PROPS */, _hoisted_4),
                    _createElementVNode("div", { class: "w-100 d-flex justify-content-center my-5" }, [
                      _createElementVNode("button", {
                        type: "button",
                        class: "btn btn-dark rounded-pill btn-lg text-white",
                        onClick: goNext
                      }, "次へ")
                    ])
                  ]))
                : _createCommentVNode("v-if", true),
              (state.value === 'course')
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    (!isFirstStep.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: "cursor-pointer font-bold",
                          onClick: goBack
                        }, "＜ 戻る"))
                      : _createCommentVNode("v-if", true),
                    _cache[1] || (_cache[1] = _createElementVNode("h3", { class: "onboard-course__title" }, " コースを選択しましょう ", -1 /* CACHED */)),
                    _cache[2] || (_cache[2] = _createElementVNode("p", { class: "onboard-course__lead" }, "気になるコースを選んでください。いくつでも選べます。", -1 /* CACHED */)),
                    _createElementVNode("div", _hoisted_6, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(userOnboard.value.courses, (item) => {
                        return (_openBlock(), _createElementBlock("label", {
                          key: item.id,
                          for: `checkbox_id_${item.id}`,
                          class: _normalizeClass(["onboard-course__card", { 'is-selected': item.selected }])
                        }, [
                          _withDirectives(_createElementVNode("input", {
                            "onUpdate:modelValue": ($event: any) => ((item.selected) = $event),
                            class: "onboard-course__input",
                            type: "checkbox",
                            id: `checkbox_id_${item.id}`,
                            value: item.id
                          }, null, 8 /* PROPS */, _hoisted_8), [
                            [_vModelCheckbox, item.selected]
                          ]),
                          _createElementVNode("div", _hoisted_9, [
                            (item.image?.url)
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  src: item.image.url,
                                  alt: ""
                                }, null, 8 /* PROPS */, _hoisted_10))
                              : _createCommentVNode("v-if", true),
                            _createElementVNode("span", _hoisted_11, [
                              _createVNode(_component_v_icon, { size: "18" }, {
                                default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                                  _createTextVNode("mdi-check", -1 /* CACHED */)
                                ]))]),
                                _: 1 /* STABLE */
                              })
                            ])
                          ]),
                          _createElementVNode("div", _hoisted_12, _toDisplayString(item.name), 1 /* TEXT */)
                        ], 10 /* CLASS, PROPS */, _hoisted_7))
                      }), 128 /* KEYED_FRAGMENT */))
                    ]),
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("span", _hoisted_14, _toDisplayString(selectedCount.value) + "件 選択中", 1 /* TEXT */),
                      _createElementVNode("button", {
                        type: "button",
                        class: "btn btn-dark rounded-pill btn-lg text-white",
                        onClick: goNext
                      }, "次へ")
                    ])
                  ]))
                : _createCommentVNode("v-if", true),
              (state.value === 'contact_form')
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    (!isFirstStep.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: "cursor-pointer font-bold",
                          onClick: goBack
                        }, "＜ 戻る"))
                      : _createCommentVNode("v-if", true),
                    _createElementVNode("div", {
                      class: "text-center",
                      innerHTML: userOnboard.value.bodyHtml
                    }, null, 8 /* PROPS */, _hoisted_16)
                  ]))
                : _createCommentVNode("v-if", true)
            ], 2 /* CLASS */)
          ])
        ]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})